import React from 'react';

import { Modal } from '@/components/Modal';
import { PartialAsset } from '@/hooks/useAssets';
import { AssetStatus, AssetStatusLabel } from '@/types';

/**
 * Props for StatusConfirmationModal component
 */
export interface StatusConfirmationModalProps {
  /** Current confirmation state with assets and target status */
  confirmStatus: {
    data: PartialAsset[];
    status: AssetStatus;
  } | null;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when status change is confirmed */
  onConfirm: (data: PartialAsset[], status: AssetStatus) => Promise<void>;
}

/**
 * Status Confirmation Modal Component
 *
 * Displays a confirmation dialog when the user attempts to change the status
 * of one or more assets. Shows the list of affected assets and the target status.
 *
 * @param props - Component props
 * @returns Status confirmation modal
 *
 * @example
 * ```tsx
 * <StatusConfirmationModal
 *   confirmStatus={confirmStatus}
 *   onClose={() => setConfirmStatus(null)}
 *   onConfirm={async (data, status) => {
 *     await handleUpdateStatus(data, status);
 *     invalidateAssets();
 *   }}
 * />
 * ```
 */
export const StatusConfirmationModal: React.FC<
  StatusConfirmationModalProps
> = ({ confirmStatus, onClose, onConfirm }) => {
  return (
    <Modal
      title="Confirm Change Status"
      open={!!confirmStatus}
      onClose={onClose}
      footer={{
        text: 'Confirm',
        onClick: async () => {
          if (!confirmStatus) return;
          const { data, status } = confirmStatus;
          await onConfirm(data, status);
        },
      }}
    >
      <div className="pb-4">
        {confirmStatus &&
          `Are you sure you want to update the status of following assets to "${AssetStatusLabel[confirmStatus.status as AssetStatus]}"?`}
      </div>
      <div className="scrollbar-dark max-h-[300px] overflow-auto">
        {confirmStatus?.data.map(asset => (
          <div key={asset.identifier}>{asset.identifier}</div>
        ))}
      </div>
    </Modal>
  );
};
